/**
 * Streaming Service
 * Gerencia streaming de texto com controle de debounce e AbortController
 */

import type { AIStreamChunk } from './types';

export interface StreamingOptions {
  debounceMs?: number;
  onChunk?: (content: string) => void;
  onComplete?: (content: string) => void;
  onError?: (error: string) => void;
}

/**
 * Serviço de streaming de texto
 */
export class StreamingService {
  private static instance: StreamingService | null = null;
  private debounceTimer: number | null = null;
  private isActive = false;

  static getInstance(): StreamingService {
    if (!StreamingService.instance) {
      StreamingService.instance = new StreamingService();
    }
    return StreamingService.instance;
  }

  /**
   * Iniciar streaming com debounce
   */
  startStream(
    chunks: AsyncIterable<AIStreamChunk>,
    options: StreamingOptions = {}
  ): void {
    const {
      debounceMs = 150,
      onChunk,
      onComplete,
      onError,
    } = options;

    this.stopStream();
    this.isActive = true;

    this.processStream(chunks, {
      debounceMs,
      onChunk,
      onComplete,
      onError,
    });
  }

  /**
   * Parar streaming ativo
   */
  stopStream(): void {
    this.isActive = false;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  /**
   * Processar stream com debounce
   */
  private async processStream(
    chunks: AsyncIterable<AIStreamChunk>,
    options: StreamingOptions
  ): Promise<void> {
    const { debounceMs = 150, onChunk, onComplete, onError } = options;
    let lastContent = '';

    try {
      for await (const chunk of chunks) {
        if (!this.isActive) {
          break;
        }

        switch (chunk.type) {
          case 'start':
            console.log('🚀 Streaming iniciado');
            break;

          case 'chunk':
            lastContent = chunk.content;
            this.debounceChunk(lastContent, debounceMs, onChunk);
            break;

          case 'complete':
            this.stopStream();
            onComplete?.(chunk.content);
            console.log('✅ Streaming completo');
            break;

          case 'error':
            this.stopStream();
            onError?.(chunk.error || 'Erro no streaming');
            console.error('❌ Erro no streaming:', chunk.error);
            break;
        }
      }
    } catch (error) {
      this.stopStream();
      onError?.(error instanceof Error ? error.message : 'Erro desconhecido');
      console.error('❌ Erro no processamento do stream:', error);
    }
  }

  /**
   * Debounce para chunks
   */
  private debounceChunk(
    content: string,
    debounceMs: number,
    onChunk?: (content: string) => void
  ): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = window.setTimeout(() => {
      if (this.isActive) {
        onChunk?.(content);
      }
    }, debounceMs);
  }

  /**
   * Verificar se está ativo
   */
  get isStreaming(): boolean {
    return this.isActive;
  }
}

export const streamingService = StreamingService.getInstance();